// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: finds the signature Outlook added to a sent message and turns a batch of
// sent messages into import candidates for Settings → Mail → Signatures.
//
// New Outlook / OWA wraps the signature in <div id="Signature">. Classic (Word)
// Outlook drops a <a name="_MailAutoSig"> bookmark at its start and leaves the
// rest as sibling paragraphs until the reply header. Outlook for iOS/Android puts
// its signature (a full HTML card for many people) in #ms-outlook-mobile-signature,
// unless all it holds is "Get Outlook for iOS". Quoted mail carries the same markers
// prefixed with x_: someone else's never counts, but the sender's own earlier mail
// quoted under a reply does, since replies sent from Konvo had no signature of their own.

export interface MailSignatureSource {
	id: string;
	subject: string;
	sent_at: string | null;
	is_reply: boolean;
	html: string;
}

export type MailSignatureNameKind = 'full' | 'short' | 'single' | 'numbered';

export interface MailSignatureCandidate {
	/** Stable per distinct signature text. */
	key: string;
	html: string;
	/** Whitespace-collapsed plain text, for previews. */
	text: string;
	/**
	 * Suggested English name: one found → "Outlook signature"; two or more → the longest
	 * "Full", the shortest "Short", any others "Outlook signature" (numbered when several).
	 * UIs localise via name_kind.
	 */
	name: string;
	name_kind: MailSignatureNameKind;
	/** 1-based position among the in-between signatures, for the 'numbered' kind. */
	name_index: number;
	/** True when it was mostly seen on replies and forwards. */
	from_reply: boolean;
	seen: number;
	seen_new: number;
	seen_reply: number;
	last_sent_at: string | null;
	suggested_default_new: boolean;
	suggested_default_reply: boolean;
}

const REPLY_BOUNDARY_SELECTOR = [
	'#divRplyFwdMsg',
	'#appendonsend',
	'#mail-editor-reference-message-container',
	'.mail-editor-reference-message-container',
].join(', ');

const REPLY_HEADER_TEXT = /^\s*(from|van|von|de|sent|verzonden)\s*:/i;

const DROP_SELECTOR = [
	'script',
	'style',
	'iframe',
	'frame',
	'object',
	'embed',
	'form',
	'input',
	'textarea',
	'select',
	'button',
	'meta',
	'link',
	'base',
	'title',
	'head',
].join(', ');

/** Outlook mobile's default signature line, which is not a signature anyone chose. */
const MOBILE_BOILERPLATE = /^(get|download)\s+outlook\s+for\s+(ios|android)$/i;

const MOBILE_SIGNATURE_IDS = new Set(['ms-outlook-mobile-signature', 'x_ms-outlook-mobile-signature']);

function isMobileBoilerplate(element: Element): boolean {
	const text = (element.textContent ?? '').replace(/[\s\u00a0\u200b]+/g, ' ').trim();
	return text === '' ? !element.querySelector('img, table') : MOBILE_BOILERPLATE.test(text);
}

const hasDom = (): boolean => typeof DOMParser !== 'undefined';

function parse(html: string): Document {
	return new DOMParser().parseFromString(html, 'text/html');
}

function isBoundary(element: Element): boolean {
	if (element.matches(REPLY_BOUNDARY_SELECTOR)) return true;
	if (element.tagName === 'HR') {
		// A signature may use a rule as a divider; only the one that opens the quoted mail counts.
		if (element.hasAttribute('tabindex')) return true;
		const next = element.nextElementSibling;
		return Boolean(next && (next.id === 'divRplyFwdMsg' || REPLY_HEADER_TEXT.test(next.textContent ?? '')));
	}
	// Classic Outlook's "From: … Sent: …" header div: no border except a top rule.
	const style = (element.getAttribute('style') ?? '').toLowerCase().replace(/\s+/g, '');
	return element.tagName === 'DIV' && style.includes('border-top:solid') && style.includes('border:none');
}

function firstBoundary(root: ParentNode): Element | null {
	for (const element of Array.from(root.querySelectorAll('*'))) {
		if (isBoundary(element)) return element;
	}
	return null;
}

function precedes(a: Node, b: Node): boolean {
	// eslint-disable-next-line no-bitwise
	return (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0 && !a.contains(b);
}

/** Removes `boundary` and everything after it inside `root`. */
function truncateAt(root: Element, boundary: Element): void {
	let node: Node | null = boundary;
	while (node && node !== root) {
		let next = node.nextSibling;
		while (next) {
			const after: ChildNode | null = next.nextSibling;
			next.remove();
			next = after;
		}
		const parent: Node | null = node.parentNode;
		if (node === boundary) (node as Element).remove();
		node = parent;
	}
}

function unwrap(element: Element): void {
	const parent = element.parentNode;
	if (!parent) return;
	while (element.firstChild) parent.insertBefore(element.firstChild, element);
	element.remove();
}

function isEmptyBlock(node: Node): boolean {
	if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? '').replace(/[\s\u00a0\u200b]+/g, '') === '';
	if (node.nodeType === Node.COMMENT_NODE) return true;
	if (node.nodeType !== Node.ELEMENT_NODE) return false;
	const element = node as Element;
	if (element.matches('img, table, hr') || element.querySelector('img, table, hr')) return false;
	return (element.textContent ?? '').replace(/[\s\u00a0\u200b]+/g, '') === '';
}

function trimEmptyEdges(root: Element): void {
	while (root.lastChild && isEmptyBlock(root.lastChild)) root.lastChild.remove();
	while (root.firstChild && isEmptyBlock(root.firstChild)) root.firstChild.remove();
	// A single wrapper (e.g. OWA's inner div) can hide empty paragraphs one level down.
	if (root.childNodes.length === 1 && root.firstChild?.nodeType === Node.ELEMENT_NODE) {
		const only = root.firstChild as Element;
		if (only.matches('div, span, font')) trimEmptyEdges(only);
	}
}

function isTrackingPixel(image: Element): boolean {
	const width = Number.parseFloat(image.getAttribute('width') ?? '');
	const height = Number.parseFloat(image.getAttribute('height') ?? '');
	if (width <= 1 && height <= 1) return true;
	const style = (image.getAttribute('style') ?? '').toLowerCase().replace(/\s+/g, '');
	const styleWidth = /(?:^|;)width:([\d.]+)px/.exec(style);
	const styleHeight = /(?:^|;)height:([\d.]+)px/.exec(style);
	return Boolean(styleWidth && styleHeight && Number(styleWidth[1]) <= 1 && Number(styleHeight[1]) <= 1);
}

/** Cleans signature HTML in place: no active content, no pixels, Word classes turned into inline margins. */
function cleanTree(root: Element): void {
	for (const node of Array.from(root.querySelectorAll(DROP_SELECTOR))) node.remove();
	for (const node of Array.from(root.querySelectorAll('div, p'))) {
		if (MOBILE_BOILERPLATE.test((node.textContent ?? '').replace(/[\s\u00a0\u200b]+/g, ' ').trim())) node.remove();
	}
	// Comments (Word's conditional comments) and the classic bookmark wrapper.
	const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
	const comments: Array<Node> = [];
	while (walker.nextNode()) comments.push(walker.currentNode);
	for (const comment of comments) comment.parentNode?.removeChild(comment);
	for (const anchor of Array.from(root.querySelectorAll('a[name]:not([href])'))) unwrap(anchor);
	for (const element of Array.from(root.querySelectorAll('*'))) {
		if (element.tagName.includes(':')) {
			// <o:p>, <v:shape>…: Office namespaces the browser does not know.
			unwrap(element);
			continue;
		}
		if (element.tagName === 'IMG') {
			const src = (element.getAttribute('src') ?? '').trim().toLowerCase();
			if (!src || src.startsWith('cid:') || isTrackingPixel(element)) {
				element.remove();
				continue;
			}
		}
		const isMsoBlock = /\bMso(Normal|ListParagraph)\b/i.test(element.getAttribute('class') ?? '');
		for (const attribute of Array.from(element.attributes)) {
			const name = attribute.name.toLowerCase();
			const value = attribute.value.trim().toLowerCase().replace(/\s+/g, '');
			if (name.startsWith('on') || name === 'id' || name === 'class' || name === 'name' || name === 'srcset') {
				element.removeAttribute(attribute.name);
			} else if (
				(name === 'href' || name === 'src' || name === 'background') &&
				/^(javascript|vbscript):/.test(value)
			) {
				element.removeAttribute(attribute.name);
			} else if (
				(name === 'src' || name === 'background') &&
				value.startsWith('data:') &&
				!value.startsWith('data:image/')
			) {
				element.removeAttribute(attribute.name);
			} else if (name === 'style' && /expression\(|javascript:|url\((?!['"]?data:image\/)/.test(value)) {
				element.removeAttribute(attribute.name);
			}
		}
		// Word styles p.MsoNormal with margin:0 in a <style> we drop; keep that spacing inline.
		if (isMsoBlock && !/(^|;)\s*margin/i.test(element.getAttribute('style') ?? '')) {
			const style = element.getAttribute('style');
			element.setAttribute('style', style ? `margin:0;${style}` : 'margin:0');
		}
	}
	trimEmptyEdges(root);
}

/** Strips scripts, handlers, javascript: URLs and non-image data: URLs. Safe to render in a div. */
export function sanitizeSignatureHtml(html: string): string {
	if (!hasDom()) {
		return html
			.replace(/<(script|style|iframe|object|embed)[\s\S]*?<\/\1\s*>/gi, '')
			.replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
			.replace(/javascript:/gi, '');
	}
	const doc = parse(`<body>${html}</body>`);
	for (const node of Array.from(doc.body.querySelectorAll(DROP_SELECTOR))) node.remove();
	for (const element of Array.from(doc.body.querySelectorAll('*'))) {
		for (const attribute of Array.from(element.attributes)) {
			const name = attribute.name.toLowerCase();
			const value = attribute.value.trim().toLowerCase().replace(/\s+/g, '');
			if (name.startsWith('on')) element.removeAttribute(attribute.name);
			else if ((name === 'href' || name === 'src') && /^(javascript|vbscript):/.test(value)) {
				element.removeAttribute(attribute.name);
			} else if (name === 'src' && value.startsWith('data:') && !value.startsWith('data:image/')) {
				element.removeAttribute(attribute.name);
			}
		}
	}
	return doc.body.innerHTML;
}

/** Plain text of signature HTML, whitespace collapsed; for one-line previews. */
export function signatureText(html: string): string {
	if (!hasDom()) {
		return html
			.replace(/<[^>]+>/g, ' ')
			.replace(/&nbsp;/g, ' ')
			.replace(/\s+/g, ' ')
			.trim();
	}
	const doc = parse(`<body>${html}</body>`);
	for (const node of Array.from(doc.body.querySelectorAll('script, style'))) node.remove();
	// Block boundaries read as separators in a one-line preview.
	for (const block of Array.from(doc.body.querySelectorAll('p, div, br, li, tr, td'))) {
		block.append(doc.createTextNode(' '));
	}
	return (doc.body.textContent ?? '').replace(/[\s\u00a0\u200b]+/g, ' ').trim();
}

function fromOwa(doc: Document): Element | null {
	const boundary = firstBoundary(doc.body);
	for (const element of Array.from(doc.body.querySelectorAll('[id]'))) {
		if (element.id.toLowerCase() !== 'signature') continue;
		if (element.closest('[id="ms-outlook-mobile-signature"]')) continue;
		if (boundary && !precedes(element, boundary)) continue;
		const wrapper = doc.createElement('div');
		for (const child of Array.from(element.childNodes)) wrapper.append(child.cloneNode(true));
		return wrapper;
	}
	return null;
}

function cloneChildren(doc: Document, element: Element): Element {
	const wrapper = doc.createElement('div');
	for (const child of Array.from(element.childNodes)) wrapper.append(child.cloneNode(true));
	return wrapper;
}

/** Outlook for iOS / Android: the whole card sits in #ms-outlook-mobile-signature. */
function fromMobile(doc: Document): Element | null {
	const boundary = firstBoundary(doc.body);
	for (const element of Array.from(doc.body.querySelectorAll('[id]'))) {
		if (element.id !== 'ms-outlook-mobile-signature') continue;
		if (boundary && !precedes(element, boundary)) continue;
		if (isMobileBoilerplate(element)) continue;
		return cloneChildren(doc, element);
	}
	return null;
}

const QUOTED_SIGNATURE_IDS = new Set(['signature', 'x_signature', ...MOBILE_SIGNATURE_IDS]);

/**
 * The sender's own signature from their earlier mail quoted further down: the reply
 * header must name them in its From line, and the block must come before the next
 * quote inside that mail.
 */
function fromQuotedSelf(doc: Document, selfAddress: string): Element | null {
	const self = selfAddress.trim().toLowerCase();
	if (!self) return null;
	for (const header of Array.from(doc.body.querySelectorAll('[id]'))) {
		if (header.id !== 'divRplyFwdMsg' && header.id !== 'x_divRplyFwdMsg') continue;
		const fromLine = (header.textContent ?? '').split(/\b(sent|date|verzonden|datum|gesendet)\s*:/i)[0] ?? '';
		if (!fromLine.toLowerCase().includes(self)) continue;
		const quoted = header.nextElementSibling;
		if (!quoted) continue;
		const inner = firstBoundary(quoted);
		for (const element of Array.from(quoted.querySelectorAll('[id]'))) {
			if (!QUOTED_SIGNATURE_IDS.has(element.id.toLowerCase())) continue;
			if (inner && !precedes(element, inner)) continue;
			if (MOBILE_SIGNATURE_IDS.has(element.id) && isMobileBoilerplate(element)) continue;
			return cloneChildren(doc, element);
		}
	}
	return null;
}

function fromClassic(doc: Document): Element | null {
	const boundary = firstBoundary(doc.body);
	for (const bookmark of Array.from(doc.body.querySelectorAll('a[name]'))) {
		if (bookmark.getAttribute('name') !== '_MailAutoSig') continue;
		if (boundary && !precedes(bookmark, boundary)) continue;
		const container = bookmark.closest('div.WordSection1') ?? doc.body;
		let start: Element = bookmark;
		while (start.parentElement && start.parentElement !== container) start = start.parentElement;
		const wrapper = doc.createElement('div');
		for (let node: Node | null = start; node; node = node.nextSibling) {
			if (node.nodeType === Node.ELEMENT_NODE && isBoundary(node as Element)) break;
			wrapper.append(node.cloneNode(true));
		}
		const inner = firstBoundary(wrapper);
		if (inner) truncateAt(wrapper, inner);
		return wrapper;
	}
	return null;
}

/**
 * The signature Outlook added to a sent message, as cleaned HTML, or null when the
 * message has none (or only "Get Outlook for iOS"). With `selfAddress`, a message
 * without one of its own can still yield the sender's signature from their earlier
 * mail quoted under it.
 */
export function extractSignatureHtml(html: string, selfAddress?: string | null): string | null {
	if (!hasDom() || !html) return null;
	const doc = parse(html);
	const found =
		fromOwa(doc) ?? fromMobile(doc) ?? fromClassic(doc) ?? (selfAddress ? fromQuotedSelf(doc, selfAddress) : null);
	if (!found) return null;
	cleanTree(found);
	if (isEmptyBlock(found)) return null;
	const out = found.innerHTML.trim();
	return out || null;
}

function signatureKey(html: string, text: string): string {
	let basis = text.replace(/\s+/g, '').toLowerCase();
	if (!basis) {
		const images = /<img[^>]*src="([^"]{0,200})/gi;
		basis = Array.from(html.matchAll(images), (match) => match[1]).join('|');
	}
	// FNV-1a, 32-bit: short and stable, enough to tell a handful of signatures apart.
	let hash = 0x811c9dc5;
	for (let i = 0; i < basis.length; i++) {
		// eslint-disable-next-line no-bitwise
		hash ^= basis.charCodeAt(i);
		hash = Math.imul(hash, 0x01000193);
	}
	// eslint-disable-next-line no-bitwise
	return `sig-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

const newer = (a: string | null, b: string | null): boolean => (a ?? '') > (b ?? '');

/** Pictures that will show: cleaned signature HTML keeps only data: and web images. */
const pictureCount = (html: string): number => (html.match(/<img\b/gi) ?? []).length;

function pickMost(candidates: Array<MailSignatureCandidate>, count: (c: MailSignatureCandidate) => number) {
	let best: MailSignatureCandidate | null = null;
	for (const candidate of candidates) {
		if (count(candidate) === 0) continue;
		if (
			!best ||
			count(candidate) > count(best) ||
			(count(candidate) === count(best) && newer(candidate.last_sent_at, best.last_sent_at))
		) {
			best = candidate;
		}
	}
	return best;
}

/**
 * Turns `/konvo/mail/signatures/sources` items into distinct signatures (deduped on
 * whitespace-insensitive text), with suggested names and suggested defaults.
 */
export function buildSignatureCandidates(
	sources: ReadonlyArray<MailSignatureSource>,
	selfAddress?: string | null,
): Array<MailSignatureCandidate> {
	const byKey = new Map<string, MailSignatureCandidate>();
	for (const source of sources) {
		const html = extractSignatureHtml(source.html, selfAddress);
		if (!html) continue;
		const text = signatureText(html);
		const key = signatureKey(html, text);
		const existing = byKey.get(key);
		const sentAt = source.sent_at ?? null;
		if (!existing) {
			byKey.set(key, {
				key,
				html,
				text,
				name: '',
				name_kind: 'single',
				name_index: 1,
				from_reply: false,
				seen: 1,
				seen_new: source.is_reply ? 0 : 1,
				seen_reply: source.is_reply ? 1 : 0,
				last_sent_at: sentAt,
				suggested_default_new: false,
				suggested_default_reply: false,
			});
			continue;
		}
		existing.seen += 1;
		if (source.is_reply) existing.seen_reply += 1;
		else existing.seen_new += 1;
		// Keep the copy with the most pictures (Outlook for iOS sometimes sends the card without
		// them), and among equals the freshest: it has the current phone number, logo, etc.
		const pictures = pictureCount(html);
		const existingPictures = pictureCount(existing.html);
		if (pictures > existingPictures || (pictures === existingPictures && newer(sentAt, existing.last_sent_at))) {
			existing.html = html;
			existing.text = text;
		}
		if (newer(sentAt, existing.last_sent_at)) existing.last_sent_at = sentAt;
	}
	const candidates = Array.from(byKey.values());
	for (const candidate of candidates) candidate.from_reply = candidate.seen_reply > candidate.seen_new;
	candidates.sort((a, b) => b.seen - a.seen || (newer(a.last_sent_at, b.last_sent_at) ? -1 : 1));

	if (candidates.length === 1) {
		candidates[0]!.name = 'Outlook signature';
		candidates[0]!.name_kind = 'single';
	} else if (candidates.length >= 2) {
		// Longest is "Full", shortest "Short"; any in between are numbered. Listed in that order.
		const byLength = [...candidates].sort((a, b) => b.text.length - a.text.length);
		const full = byLength[0]!;
		const short = byLength[byLength.length - 1]!;
		const middle = candidates.filter((c) => c !== full && c !== short);
		full.name = 'Full';
		full.name_kind = 'full';
		short.name = 'Short';
		short.name_kind = 'short';
		middle.forEach((candidate, index) => {
			candidate.name = middle.length === 1 ? 'Outlook signature' : `Outlook signature ${index + 1}`;
			candidate.name_kind = middle.length === 1 ? 'single' : 'numbered';
			candidate.name_index = index + 1;
		});
		candidates.splice(0, candidates.length, full, ...middle, short);
	}

	const forNew = pickMost(candidates, (c) => c.seen_new);
	const forReply = pickMost(candidates, (c) => c.seen_reply);
	const defaultNew = forNew ?? forReply;
	const defaultReply = forReply ?? forNew;
	if (defaultNew) defaultNew.suggested_default_new = true;
	if (defaultReply) defaultReply.suggested_default_reply = true;
	return candidates;
}
